import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useId, useMemo } from "react"
import { type Control, type FieldPath, type FieldValues, useController, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { TaskDetail } from "@/api/generated/TaskDetail"
import { useProjects } from "@/api/queries/projects"
import { type CreateTaskInput, TASK_PRIORITIES, useCreateTask, useTaskWorkers } from "@/api/queries/tasks"
import {
  FieldShell,
  type Option,
  SelectField,
  SwitchField,
  TagInputField,
  TextareaField,
  TextField,
} from "@/components/app/fields"
import { FormSheet } from "@/components/app/form"
import { Input } from "@/components/ui/input"
import { z } from "@/lib/zod"
import { fromDatetimeLocal, parseAssigneeKey } from "./logic"
import { useTaskLabels } from "./task-badges"

/** `<input type="datetime-local">` bound to a form field holding the local string. */
export function DateTimeField<T extends FieldValues, N extends FieldPath<T>>(props: {
  control: Control<T>
  name: N
  label?: React.ReactNode
  description?: React.ReactNode
  className?: string
}) {
  const id = useId()
  const { field, fieldState } = useController({ control: props.control, name: props.name })
  return (
    <FieldShell
      id={id}
      label={props.label}
      description={props.description}
      error={fieldState.error}
      className={props.className}
    >
      <Input
        id={id}
        type="datetime-local"
        ref={field.ref}
        name={field.name}
        value={(field.value as string | undefined) ?? ""}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        aria-invalid={fieldState.invalid || undefined}
      />
    </FieldShell>
  )
}

/** Options for assignee pickers: the pool, then agents, then teams. */
export function useAssigneeOptions(): Option[] {
  const { t } = useTranslation()
  const workers = useTaskWorkers()
  return useMemo(
    () => [
      { value: "pool", label: t("tasks.task.assignee.pool"), description: t("tasks.task.assignee.poolHint") },
      ...(workers.data?.agents ?? []).map((a) => ({
        value: `agent:${a.id}`,
        label: a.name,
        description: t("tasks.task.assignee.agent"),
      })),
      ...(workers.data?.teams ?? []).map((tm) => ({
        value: `team:${tm.id}`,
        label: tm.name,
        description: t("tasks.task.assignee.team"),
      })),
    ],
    [workers.data, t],
  )
}

/** Non-archived projects (the same cached list the Projects page shows). */
export function useProjectOptions(): Option<number>[] {
  const projects = useProjects()
  return useMemo(() => (projects.data ?? []).map((p) => ({ value: p.id, label: p.name })), [projects.data])
}

export function usePriorityOptions(): Option[] {
  const labels = useTaskLabels()
  return TASK_PRIORITIES.map((p) => ({ value: p, label: labels.priority(p) }))
}

const schema = z.object({
  title: z.string().trim().min(1).max(255),
  description: z.string().max(20_000),
  expected_output: z.string().max(20_000),
  project_id: z.number().nullable(),
  priority: z.enum(TASK_PRIORITIES),
  assignee: z.string().nullable(),
  due_at: z.string(),
  labels: z.array(z.string()),
  required_inputs: z.array(z.string()),
  auto_start: z.boolean(),
})

type Values = z.infer<typeof schema>

export interface TaskCreateDefaults {
  project_id?: number | null
  assignee?: string | null
}

const blank = (d: TaskCreateDefaults = {}): Values => ({
  title: "",
  description: "",
  expected_output: "",
  project_id: d.project_id ?? null,
  priority: "normal",
  assignee: d.assignee ?? null,
  due_at: "",
  labels: [],
  required_inputs: [],
  auto_start: false,
})

/** Builds the `POST /tasks` body from the form values. */
export function toCreateBody(v: Values): CreateTaskInput {
  const assignee = parseAssigneeKey(v.assignee)
  return {
    title: v.title.trim(),
    description: v.description,
    expected_output: v.expected_output,
    project_id: v.project_id ?? undefined,
    priority: v.priority,
    due_at: fromDatetimeLocal(v.due_at),
    labels: v.labels,
    auto_start: v.auto_start,
    input_spec: v.required_inputs.length
      ? v.required_inputs.map((name) => ({ name, required: true }))
      : undefined,
    ...(assignee ?? {}),
  }
}

/** Side sheet to create a task (optionally pre-filled with a project/assignee). */
export function TaskCreateSheet({
  open,
  onOpenChange,
  defaults,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaults?: TaskCreateDefaults
  onCreated?: (task: TaskDetail) => void
}) {
  const { t } = useTranslation()
  const create = useCreateTask()
  const assigneeOptions = useAssigneeOptions()
  const projectOptions = useProjectOptions()
  const priorityOptions = usePriorityOptions()
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: blank(defaults) })

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the sheet opens
  useEffect(() => {
    if (open) form.reset(blank(defaults))
  }, [open])

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      title={t("tasks.task.create.title")}
      description={t("tasks.task.create.description")}
      submitLabel={t("tasks.task.create.submit")}
      className="sm:max-w-xl"
      onSubmit={async (v) => {
        const task = await create.mutateAsync(toCreateBody(v))
        onOpenChange(false)
        onCreated?.(task)
      }}
    >
      <TextField
        control={form.control}
        name="title"
        label={t("tasks.task.fields.title")}
        required
        autoFocus
      />
      <TextareaField
        control={form.control}
        name="description"
        label={t("tasks.task.fields.description")}
        placeholder={t("tasks.task.fields.descriptionPlaceholder")}
        rows={4}
      />
      <TextareaField
        control={form.control}
        name="expected_output"
        label={t("tasks.task.fields.expectedOutput")}
        placeholder={t("tasks.task.fields.expectedOutputPlaceholder")}
        rows={2}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          control={form.control}
          name="assignee"
          label={t("tasks.task.fields.assignee")}
          options={assigneeOptions}
          nullable
          noneLabel={t("tasks.task.assignee.unassigned")}
        />
        <SelectField
          control={form.control}
          name="project_id"
          label={t("tasks.task.fields.project")}
          options={projectOptions}
          nullable
          noneLabel={t("tasks.task.fields.noProject")}
        />
        <SelectField
          control={form.control}
          name="priority"
          label={t("tasks.task.fields.priority")}
          options={priorityOptions}
        />
        <DateTimeField control={form.control} name="due_at" label={t("tasks.task.fields.due")} />
      </div>
      <TagInputField
        control={form.control}
        name="labels"
        label={t("tasks.task.fields.labels")}
        placeholder={t("tasks.task.fields.labelsPlaceholder")}
      />
      <TagInputField
        control={form.control}
        name="required_inputs"
        lowercase={false}
        label={t("tasks.task.fields.requiredInputs")}
        description={t("tasks.task.fields.requiredInputsHint")}
        placeholder={t("tasks.task.fields.requiredInputsPlaceholder")}
      />
      <SwitchField
        control={form.control}
        name="auto_start"
        variant="card"
        label={t("tasks.task.fields.autoStart")}
        description={t("tasks.task.fields.autoStartHint")}
      />
    </FormSheet>
  )
}
